import React from 'react';
import { COLOR } from '../settings/Global';
import { createHomeUrl } from '../utils/urlHelper';
import { LOZENGE_BORDER_RADIUS } from './Lozenge';
import ErrorBoundary from './ErrorBoundary';

const buttonStyle: React.CSSProperties = {
    font: 'inherit',
    color: COLOR.BLACK,
    backgroundColor: COLOR.WHITE,
    border: 'none',
    borderRadius: `${LOZENGE_BORDER_RADIUS}px`,
    padding: '0.5em 1.25em',
    cursor: 'pointer',
};

/**
 * Shown when the layout itself fails to render. It's outside the layout, so it
 * can't use the theme, fonts, global styles or the error overlay, and only
 * uses inline styles, so that it can't fail itself.
 */
function SiteError(): React.ReactElement {
    return (
        <div
            style={{
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                gap: '1.5rem',
                padding: '2rem',
                color: COLOR.WHITE,
                backgroundColor: COLOR.BLACK,
                fontFamily: 'system-ui, sans-serif',
                fontSize: '1.25rem',
                lineHeight: 1.4,
            }}
        >
            <h1 style={{ margin: 0, fontSize: '2rem', fontWeight: 'normal' }}>
                Something has gone wrong.
                <br />
                No need to panic though.
            </h1>
            <p style={{ margin: 0 }}>
                To get back on track try reloading the page or check back soon.
            </p>
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                <button
                    type="button"
                    style={buttonStyle}
                    onClick={(): void => window.location.reload()}
                >
                    Refresh page
                </button>
                <a href={createHomeUrl()} style={{ color: COLOR.WHITE }}>
                    Return home
                </a>
            </div>
        </div>
    );
}

/**
 * For `wrapPageElement` in both gatsby-browser and gatsby-ssr, so the server
 * and browser render the same tree.
 *
 * Stops an error in the layout, e.g. in the navigation or footer, from
 * unmounting the whole site, which would leave a blank page. Errors in pages
 * are caught by `PageErrorBoundary` first. Keyed, so that navigating to
 * another page clears an error.
 */
export function wrapPageElementWithErrorBoundary({
    element,
    props,
}: {
    element: React.ReactNode;
    props: { location: { pathname: string } };
}): React.ReactElement {
    return (
        <ErrorBoundary key={props.location.pathname} fallback={<SiteError />}>
            {element}
        </ErrorBoundary>
    );
}
